import "core-js/modules/es.array.concat.js";
import { createTheme as createMuiTheme } from '@material-ui/core';
import merge from 'lodash/merge';
import { COLORS } from "../constants/colors/index";
import { GRAY, BLUE, RED } from "../constants/colors/hex";

/*

Documentation for Material Themeing can be found here: https://material-ui.com/customization/theming/

These styles are used on anything wrapped in a <ThemeProvider theme={THIS_EXPORTED_THEME}>

props - so that a prop is passed to a component by default without the dev having to add the prop manually,
adding the prop manually will override this default value

palette - the colors use by material via the color prop on buttons and other components

override - global style settings, these can overrided on a component basis via styled-components and wrapping the styling
in a ` && { STYLE GOES HERE } ` the && increases specificity to make sure that's the styling used

*/

export function createTheme() {
  var customTheme = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return createMuiTheme(merge({
    overrides: {
      MuiAutocomplete: {
        option: {
          color: "".concat(GRAY[5], "!important"),
          '&:active': {
            backgroundColor: "".concat(GRAY[20], "!important")
          },
          '&:hover': {
            backgroundColor: 'white'
          },
          '&$selected': {
            backgroundColor: BLUE[19],
            color: BLUE[5],
            '&:hover': {
              backgroundColor: BLUE[19]
            }
          }
        }
      },
      MuiBreadcrumbs: {
        li: {
          color: GRAY[5],
          '&:last-of-type': {
            fontWeight: 600
          }
        }
      },
      MuiButton: {
        root: {
          boxShadow: 'none',
          textTransform: 'none',
          '&:focus': {
            outline: 'none'
          }
        },
        textSizeSmall: {
          fontWeight: 400
        },
        textPrimary: {},
        containedPrimary: {},
        outlinedPrimary: {},
        textSecondary: {},
        containedSecondary: {},
        outlinedSecondary: {},
        outlined: {},
        contained: {
          boxShadow: 'none'
        },
        sizeSmall: {
          fontFamily: 'Open Sans',
          padding: '4px 16px',
          borderRadius: '8px'
        },
        sizeLarge: {
          fontFamily: 'Open Sans',
          padding: '8px 16px',
          borderRadius: '8px'
        }
      },
      MuiButtonGroup: {
        root: {
          boxShadow: 'none!important'
        }
      },
      MuiCard: {
        root: {
          borderRadius: '8px',
          padding: '16px'
        }
      },
      MuiCheckbox: {
        root: {
          borderRadius: '12px',
          padding: '4px'
        }
      },
      MuiFormHelperText: {
        root: {
          '&$error': {
            color: RED[9]
          }
        }
      },
      MuiInput: {},
      MuiInputLabel: {
        outlined: {
          transform: 'translate(16px, 13px) scale(1)'
        },
        shrink: {
          top: '-3px',
          fontFamily: 'Open Sans',
          fontWeight: 400,
          fontSize: '14px',
          lineHeight: '24px'
        }
      },
      MuiInputBase: {
        root: {
          height: '40px'
        },
        input: {
          fontFamily: 'Open Sans',
          fontWeight: 400,
          fontSize: '16px',
          lineHeight: '24px',
          padding: '8px 16px'
        },
        multiline: {
          height: 'auto'
        },
        inputHiddenLabel: false
      },
      MuiListItem: {
        button: {
          color: "".concat(GRAY[5], "!important"),
          '&:active': {
            backgroundColor: "".concat(GRAY[20], "!important")
          },
          '&:hover': {
            backgroundColor: 'white'
          },
          '&$selected': {
            backgroundColor: BLUE[19],
            color: BLUE[5],
            '&:hover': {
              backgroundColor: BLUE[19]
            }
          }
        }
      },
      MuiMenu: {
        list: {
          paddingTop: 0,
          paddingBottom: 0
        }
      },
      MuiOutlinedInput: {
        root: {
          borderRadius: '4px'
        },
        notchedOutline: {
          '& span': {
            fontSize: '14px'
          }
        }
      },
      MuiPaper: {
        root: {
          backgroundColor: GRAY[21]
        },
        rounded: {
          borderRadius: 0
        },
        elevation1: {
          marginTop: 8,
          boxShadow: "0px 2px 8px ".concat(COLORS.boxShadow.shadow1, ", 0px 0px 2px ").concat(COLORS.boxShadow.shadow2, ", 0px 2px 4px ").concat(COLORS.boxShadow.shadow3),
          '&:hover': {
            backgroundColor: GRAY[21]
          },
          borderRadius: 8
        }
      },
      MuiPickersCalendarHeader: {
        iconButton: {
          backgroundColor: 'transparent'
        }
      },
      MuiPickersDay: {
        day: {
          '&:focus': {
            outline: 'none'
          },
          '&:active': {
            outline: 'none'
          }
        }
      },
      MuiPopover: {
        paper: {
          borderRadius: '8px',
          backgroundColor: GRAY[21],
          boxShadow: "0px 2px 8px ".concat(COLORS.boxShadow.shadow1, ", 0px 0px 2px ").concat(COLORS.boxShadow.shadow2, ", 0px 2px 4px ").concat(COLORS.boxShadow.shadow3),
          '&:hover': {
            backgroundColor: GRAY[21]
          }
        }
      },
      MuiSelect: {
        select: {
          '&:focus': {
            backgroundColor: 'initial'
          }
        }
      },
      MuiSlider: {
        root: {
          display: 'flex',
          alignItems: 'center'
        },
        markActive: {
          backgroundColor: 'transparent'
        },
        mark: {
          backgroundColor: 'transparent',
          borderTop: "solid ".concat(GRAY[15], " 9px"),
          borderBottom: "solid ".concat(GRAY[15], " 9px"),
          height: '6px',
          width: 1
        },
        thumb: {
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          marginTop: '0px',
          marginLeft: '-12px',
          zIndex: 2,
          backgroundColor: BLUE[21],
          boxSizing: 'border-box',
          border: "solid 2px ".concat(COLORS.primary.main)
        },
        track: {
          zIndex: 2,
          backgroundColor: COLORS.primary.main
        },
        rail: {
          backgroundColor: GRAY[15],
          backgroundClip: 'content-box',
          height: '2px',
          opacity: 1,
          borderTop: 'solid transparent 2px',
          borderBottom: 'solid transparent 2px',
          borderRadius: 2,
          boxSizing: 'content-box',
          zIndex: 1
        }
      },
      MuiTab: {
        root: {
          textTransform: 'default',
          fontWeight: 400,
          fontStyle: 'normal',
          fontSize: '16px',
          lineHeight: '24px',
          fontFamily: 'Open Sans',
          '&:focus': {
            outline: 'none'
          }
        }
      },
      MuiTableCell: {
        root: {
          borderBottom: 'none',
          padding: '16px'
        },
        head: {
          color: GRAY[10],
          letterSpacing: '1px',
          fontFamily: "'Open Sans', sans-serif;",
          fontWeight: 400,
          fontSize: '14px',
          lineHeight: '24px',
          textTransform: 'uppercase'
        },
        body: {
          color: GRAY[5],
          fontFamily: "'Open Sans', sans-serif;",
          fontWeight: 400,
          fontSize: '16px',
          lineHeight: '24px'
        }
      },
      MuiTableContainer: {
        root: {
          backgroundColor: GRAY[21],
          border: "1px solid ".concat(GRAY[18]),
          borderRadius: '8px'
        }
      },
      MuiTableRow: {
        root: {
          '&:not(:last-of-type)': {
            borderBottom: "1px solid ".concat(GRAY[18])
          }
        },
        head: {
          borderBottom: "1px solid ".concat(GRAY[18])
        }
      },
      MuiTabs: {
        indicator: {
          height: '4px',
          borderRadius: '20px 20px 0px 0px'
        },
        root: {
          borderBottom: "1px solid ".concat(GRAY[18])
        }
      },
      MuiTextField: {
        root: {
          display: 'inline-flex',
          justifyContent: 'center',
          boxSizing: 'border-box'
        }
      },
      MuiTooltip: {
        tooltip: {
          display: 'flex',
          alignItems: 'center',
          backgroundColor: GRAY[21],
          borderRadius: '8px',
          padding: '16px',
          color: GRAY[5],
          boxShadow: '0px 1px 6px rgba(59, 65, 83, 0.17), 0px 0px 2px rgba(59, 65, 83, 0.12), 0px 2px 4px rgba(59, 65, 83, 0.1);'
        }
      },
      PrivateNotchedOutline: {
        legendLabelled: {
          '& span': {
            padding: '0px',
            paddingRight: '1px',
            lineHeight: 'normal'
          }
        }
      }
    },
    palette: {
      error: {
        main: COLORS.negative.main
      },
      primary: {
        main: COLORS.primary.main
      },
      secondary: {
        main: COLORS.secondary.main
      },
      success: {
        main: COLORS.positive.main
      },
      text: {
        // primary: "#FFFFFF",
      },
      warning: {
        main: COLORS.warning.main
      }
    },
    props: {
      MuiButton: {
        disableElevation: true
      },
      MuiButtonBase: {
        disableRipple: true
      },
      MuiButtonGroup: {
        disableRipple: true
      },
      MuiCheckbox: {
        disableRipple: true
      },
      MuiInput: {
        disableUnderline: true
      },
      MuiInputLabel: {},
      MuiOutlinedInput: {
        // notched: true,
      },
      MuiMenuItem: {
        className: 'menu-item',
        style: {
          appearance: {},
          color: '#0064E1',
          padding: '8px 16px'
        }
      },
      MuiMenuList: {
        style: {
          borderRadius: '0px'
        }
      },
      MuiSelect: {
        MenuProps: {
          MenuListProps: {}
        }
      }
    },
    typography: {
      h5: {
        fontFamily: 'Montserrat',
        fontWeight: 600,
        fontSize: '20px',
        lineHeight: '32px',
        color: GRAY[5]
      }
    }
  }, customTheme));
}